"use client";

import type * as React from "react";

import { cx } from "../../lib/cx";
import { Pagination } from "./Pagination";
import s from "./DataTable.module.css";

export interface DataTableColumn<T = unknown> {
  key: string;
  /** El texto de la cabecera; también un nodo (la casilla de "seleccionar todas"). */
  header: React.ReactNode;
  align?: "left" | "right" | "center";
  /** Cifras con `--font-mono` y tabular-nums. */
  mono?: boolean;
  /** Se oculta en celular (`< 768px`). La primera columna nunca se oculta. */
  hideBelow?: "md";
  /** Ancho de la columna (`"34%"`, `"160px"`). Con alguno definido la tabla pasa a `table-layout: fixed`. */
  width?: string;
  /** Habilita el control de orden en el encabezado. */
  sortable?: boolean;
  // Firma de método y no de propiedad a propósito: es bivariante, así que
  // `DataTableColumn[]` acepta un `render: (fila: Tipo) => …` sin castear.
  render?(row: T): React.ReactNode;
}

export type DataTableRowHighlight = "brand" | "muted" | "warning";

interface DataTablePaginationProps {
  /** Página actual (desde 1). */
  page: number;
  /** Cantidad de filas por página. */
  pageSize: number;
  /** Cantidad total de filas. */
  total: number;
  onPageChange: (page: number) => void;
}

interface DataTableWithoutPagination {
  page?: never;
  pageSize?: never;
  total?: never;
  onPageChange?: never;
}

export type DataTableProps<T> = Omit<React.ComponentPropsWithoutRef<"div">, "children"> &
  (DataTablePaginationProps | DataTableWithoutPagination) & {
  columns?: DataTableColumn<T>[];
  rows?: T[];
  /** Fila clickeable con puntero, foco y Enter. */
  onRowClick?: (row: T) => void;
  /** Fila navegable: el contenido de la primera celda es un enlace que se extiende a la fila entera. */
  rowHref?: (row: T) => string;
  /** El componente del enlace, p. ej. `Link` de Next. Por default `<a>`. */
  linkAs?: React.ElementType;
  rowKey?: (row: T, index: number) => string | number;
  /** Qué filas van con un tinte: `true` o `"brand"` es el violeta apenas perceptible (la fila propia en una lista de
   *  personas); `"muted"` la apaga (una asignación ya cerrada); `"warning"` la pone en ámbar (una confirmación en
   *  curso dentro de la fila). */
  rowHighlight?: (row: T) => boolean | DataTableRowHighlight | undefined;
  /** Qué mostrar cuando no hay filas. */
  empty?: React.ReactNode;
  /** Dibuja filas fantasma mientras llegan los datos. */
  loading?: boolean;
  /** Clave y dirección del orden controlado. */
  sortKey?: string;
  sortDirection?: "asc" | "desc";
  /** Informa el próximo orden pedido desde una cabecera ordenable. */
  onSort?: (key: string, direction: "asc" | "desc") => void;
  /** card = con marco propio (default). flush = sin marco, para vivir adentro de una `Card`: cabecera en sentence
   *  case con regla arriba, 24px en los bordes y hover en todas las filas. */
  variant?: "card" | "flush";
  /** Ancho mínimo de la tabla; por debajo, se desplaza a lo ancho dentro del marco. */
  minWidth?: string;
};

const FILAS_FANTASMA = 5;

function direccionAria(estaOrdenada: boolean, direccion?: "asc" | "desc"): "none" | "ascending" | "descending" {
  if (!estaOrdenada) return "none";
  return direccion === "desc" ? "descending" : "ascending";
}

function indicadorOrden(estaOrdenada: boolean, direccion?: "asc" | "desc"): string {
  if (!estaOrdenada) return "↕";
  return direccion === "desc" ? "↓" : "↑";
}

function claveDefault<T>(row: T, index: number): string | number {
  const id = (row as { id?: string | number } | null)?.id;
  return id ?? index;
}

function celdaDefault<T>(row: T, key: string): React.ReactNode {
  const valor = (row as Record<string, unknown>)[key];
  return typeof valor === "string" || typeof valor === "number" ? valor : null;
}

/** Tabla compacta: cabecera hundida en mayúsculas, hairlines entre filas, columnas mono para cifras. */
export function DataTable<T>({
  columns = [],
  rows = [],
  onRowClick,
  rowHref,
  linkAs: Enlace = "a",
  rowKey = claveDefault,
  rowHighlight,
  empty,
  loading = false,
  sortKey,
  sortDirection,
  onSort,
  page,
  pageSize,
  total,
  onPageChange,
  variant = "card",
  minWidth,
  className,
  ...rest
}: DataTableProps<T>) {
  const interactiva = Boolean(onRowClick);
  const totalPaginas = pageSize && typeof total === "number" ? Math.ceil(total / pageSize) : 0;
  const conAnchos = columns.some((c) => c.width);

  function cabeceraOrdenable(columna: DataTableColumn<T>) {
    const estaOrdenada = sortKey === columna.key;
    const proximaDireccion = estaOrdenada && sortDirection === "asc" ? "desc" : "asc";
    const indicador = indicadorOrden(estaOrdenada, sortDirection);

    return (
      <button type="button" className={s.orden} onClick={() => onSort?.(columna.key, proximaDireccion)} disabled={!onSort}>
        {columna.header}
        <span aria-hidden="true">{indicador}</span>
      </button>
    );
  }

  return (
    <div className={cx(s.marco, className)} data-variant={variant} {...rest}>
      {/* El ancho mínimo se mide en runtime: va como custom property, no como clase. */}
      <table
        className={s.tabla}
        data-fixed={conAnchos || undefined}
        style={minWidth ? ({ "--_min": minWidth } as React.CSSProperties) : undefined}
      >
        {conAnchos ? (
          <colgroup>
            {columns.map((c, j) => (
              <col key={c.key} data-hide={j === 0 ? undefined : c.hideBelow} style={c.width ? { width: c.width } : undefined} />
            ))}
          </colgroup>
        ) : null}
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={s.th}
                data-align={c.align}
                data-hide={c.hideBelow}
                scope="col"
                aria-sort={c.sortable ? direccionAria(sortKey === c.key, sortDirection) : undefined}
              >
                {c.sortable ? cabeceraOrdenable(c) : c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: FILAS_FANTASMA }, (_, i) => (
                <tr key={`fantasma-${i}`} className={s.fila} aria-hidden="true">
                  {columns.map((c) => (
                    <td key={c.key} className={s.td} data-hide={c.hideBelow}>
                      <span className={s.fantasma} />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((r, i) => {
                const href = rowHref?.(r);
                const tinte = rowHighlight?.(r);
                return (
                  <tr
                    key={rowKey(r, i)}
                    className={s.fila}
                    data-interactive={interactiva || href ? "" : undefined}
                    data-highlight={tinte === true ? "brand" : tinte || undefined}
                    tabIndex={interactiva ? 0 : undefined}
                    onClick={interactiva ? () => onRowClick?.(r) : undefined}
                    onKeyDown={
                      interactiva
                        ? (e) => {
                            if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                              e.preventDefault();
                              onRowClick?.(r);
                            }
                          }
                        : undefined
                    }
                  >
                    {columns.map((c, j) => {
                      const contenido = c.render ? c.render(r) : celdaDefault(r, c.key);
                      return (
                        <td
                          key={c.key}
                          className={s.td}
                          data-align={c.align}
                          data-mono={c.mono || undefined}
                          data-hide={j === 0 ? undefined : c.hideBelow}
                        >
                          {j === 0 && href ? (
                            <Enlace href={href} className={s.enlaceFila}>
                              {contenido}
                            </Enlace>
                          ) : (
                            contenido
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
        </tbody>
      </table>
      {!loading && rows.length === 0 && empty ? <div className={s.vacio}>{empty}</div> : null}
      {!loading && page && totalPaginas > 1 ? (
        <div className={s.paginado}>
          <Pagination page={page} total={totalPaginas} onChange={onPageChange} />
        </div>
      ) : null}
    </div>
  );
}
